import { Polygon as OlPolygon, LineString as OlLineString } from 'ol/geom';
import { polygon as turfPolygon, kinks } from '@turf/turf';
import { transform } from 'ol/proj';
import {
  validateCoordinateRange,
  MAX_LONGITUDE,
  MIN_LONGITUDE,
} from './antimeridian';

export type PolygonValidationResult = {
  hasSelfIntersection: boolean;
  intersectionCount: number;
  hasExcessiveLongitudeSpan: boolean;
  longitudeSpan: number;
  hasOutOfRangeCoordinates: boolean;
  crossesAntimeridian: boolean;
};

export type LineStringValidationResult = {
  hasExcessiveLongitudeSpan: boolean;
  longitudeSpan: number;
  hasOutOfRangeCoordinates: boolean;
  crossesAntimeridian: boolean;
};

/**
 * Calculates the longitude span of a polygon, handling both:
 * 1. OpenLayers "world coordinates" where longitudes can exceed ±180° (e.g., 305° instead of -55°)
 * 2. Normal coordinates where we need to detect antimeridian crossings
 *
 * OpenLayers uses extended coordinates when the user pans the map past the antimeridian.
 * In this case, the raw min/max difference gives us the true span.
 *
 * For normal coordinates (within [-180, 180]), we normalize around the first point
 * to calculate the "short way" span, matching the logic in calculateExtentWithAntimeridianSupport.
 *
 * @param coordinates - Array of [lon, lat] coordinate pairs forming the polygon ring
 * @returns The longitude span in degrees (always positive)
 */
export function getLongitudeSpan(coordinates: number[][]): number {
  if (coordinates.length < 2) return 0;

  const longitudes = coordinates.map((coord) => coord[0]);
  const minLon = Math.min(...longitudes);
  const maxLon = Math.max(...longitudes);

  // Check if any coordinates are outside the normal [-180, 180] range
  // This indicates OpenLayers "world coordinates" from panning past the antimeridian
  const hasExtendedCoordinates = longitudes.some(
    (lon) => lon < -180 || lon > 180
  );

  if (hasExtendedCoordinates) {
    // Use raw min/max difference - OpenLayers already preserved the user's intent
    const span = maxLon - minLon;
    return span;
  }

  // For normal coordinates within [-180, 180], normalize around the first point
  // to get the "short way" span (same logic as calculateExtentWithAntimeridianSupport)
  const normalizeLongitude = (lon: number, baseLon: number): number => {
    while (lon - baseLon > 180) lon -= 360;
    while (baseLon - lon > 180) lon += 360;
    return lon;
  };

  const firstLon = coordinates[0][0];
  let normalizedMin = firstLon;
  let normalizedMax = firstLon;

  for (const coord of coordinates) {
    const normalizedLon = normalizeLongitude(coord[0], firstLon);
    normalizedMin = Math.min(normalizedMin, normalizedLon);
    normalizedMax = Math.max(normalizedMax, normalizedLon);
  }

  const span = normalizedMax - normalizedMin;

  return span;
}

/**
 * Validates polygon coordinates for self-intersections and excessive longitude span.
 *
 * Self-intersecting polygons can cause rendering issues where the polygon
 * appears as a thin stripe or renders the exterior instead of the interior.
 *
 * Polygons spanning more than 360° longitude cover more than the entire world,
 * which is almost certainly an error (user accidentally crossed the antimeridian
 * while trying to draw a large region).
 *
 * Note: Polygons that cross the antimeridian "the short way" (e.g., Alaska region
 * from 170° to -170°) are valid and won't trigger this warning.
 *
 * @param coordinates - Polygon coordinates as nested arrays [[[lon, lat], ...]]
 * @returns Object with validation results
 */
export function checkPolygonSelfIntersection(
  coordinates: number[][][]
): PolygonValidationResult {
  const result: PolygonValidationResult = {
    hasSelfIntersection: false,
    intersectionCount: 0,
    hasExcessiveLongitudeSpan: false,
    longitudeSpan: 0,
    hasOutOfRangeCoordinates: false,
    crossesAntimeridian: false,
  };

  try {
    if (!coordinates || coordinates.length === 0 || coordinates[0].length < 4) {
      return result;
    }

    const outerRing = coordinates[0];

    // Check for coordinates outside ±540° range
    const rangeValidation = validateCoordinateRange(coordinates);
    result.hasOutOfRangeCoordinates = !rangeValidation.isValid;

    // Calculate longitude span (handles both extended and normal coordinates)
    result.longitudeSpan = getLongitudeSpan(outerRing);
    // Flag if span exceeds world width (360°) - this means the polygon
    // wraps around the entire world, which is almost certainly an error
    result.hasExcessiveLongitudeSpan = result.longitudeSpan > 360;

    // Check if any coordinates are outside [-180, 180] range (indicates antimeridian crossing)
    const longitudes = outerRing.map((coord) => coord[0]);
    result.crossesAntimeridian = longitudes.some(
      (lon) => lon < -180 || lon > 180
    );

    // Check for self-intersections using Turf.js
    // Since we use transform() instead of toLonLat(), coordinates are preserved as extended
    // (e.g., 190° instead of -170°), so edges are continuous and Turf.js works correctly
    // even for antimeridian-crossing polygons.
    const polygon = turfPolygon(coordinates);
    const kinksResult = kinks(polygon);
    result.intersectionCount = kinksResult.features.length;
    result.hasSelfIntersection = result.intersectionCount > 0;

    return result;
  } catch {
    // If Turf.js fails to process the polygon, return current result
    return result;
  }
}

/**
 * Checks if an OpenLayers Polygon geometry has validation issues.
 *
 * @param geometry - OpenLayers Polygon geometry to check
 * @returns Object with validation results
 */
export function hasSelfIntersection(geometry: OlPolygon): PolygonValidationResult {
  const rawCoordinates = geometry.getCoordinates();
  
  // Transform coordinates from Web Mercator (EPSG:3857) to Geographic (EPSG:4326)
  // Use transform() instead of toLonLat() to preserve extended coordinates (±540°)
  // toLonLat normalizes to [-180, 180] which breaks antimeridian detection
  const coordinates = rawCoordinates.map(ring =>
    ring.map(coord => transform(coord, 'EPSG:3857', 'EPSG:4326') as number[])
  );

  return checkPolygonSelfIntersection(coordinates);
}

/**
 * Gets warning messages for polygon validation issues.
 * Single source of truth for polygon warnings - use this instead of 
 * manually checking validation results.
 *
 * @param coordinates - Polygon coordinates in EPSG:4326 (lon/lat degrees)
 * @returns Warning message string or null if no warnings
 */
export function getPolygonWarnings(coordinates: number[][][]): string | null {
  const result = checkPolygonSelfIntersection(coordinates);
  const warnings: string[] = [];

  if (result.crossesAntimeridian) {
    warnings.push(
      'This shape appears to cross the International Date Line (180° longitude).'
    );
  }
  if (result.hasSelfIntersection) {
    warnings.push('This polygon has self-intersecting edges.');
  }
  if (result.hasExcessiveLongitudeSpan) {
    warnings.push(
      'This polygon spans more than the entire world. Did you accidentally cross the antimeridian?'
    );
  }
  if (result.hasOutOfRangeCoordinates) {
    warnings.push(
      `Coordinates must be within ±${MAX_LONGITUDE}° longitude. Some coordinates are outside this range.`
    );
  }

  return warnings.length > 0 ? warnings.join(' ') : null;
}

// =============================================================================
// LINESTRING VALIDATION
// =============================================================================

/**
 * Calculates the longitude span of a linestring.
 * Uses the same logic as getLongitudeSpan but for LineString coordinate structure.
 *
 * @param coordinates - Array of [lon, lat] coordinate pairs
 * @returns The longitude span in degrees (always positive)
 */
export function getLineStringLongitudeSpan(coordinates: number[][]): number {
  return getLongitudeSpan(coordinates);
}

/**
 * Validates LineString coordinates for excessive longitude span and out-of-range coordinates.
 *
 * @param coordinates - LineString coordinates as array of [lon, lat] pairs
 * @returns Object with validation results
 */
export function checkLineStringValidation(
  coordinates: number[][]
): LineStringValidationResult {
  const result: LineStringValidationResult = {
    hasExcessiveLongitudeSpan: false,
    longitudeSpan: 0,
    hasOutOfRangeCoordinates: false,
    crossesAntimeridian: false,
  };

  try {
    if (!coordinates || coordinates.length < 2) {
      return result;
    }

    // Check for coordinates outside ±540° range
    const rangeValidation = validateCoordinateRange(coordinates);
    result.hasOutOfRangeCoordinates = !rangeValidation.isValid;

    // Calculate longitude span
    result.longitudeSpan = getLineStringLongitudeSpan(coordinates);
    result.hasExcessiveLongitudeSpan = result.longitudeSpan > 360;

    // Check if any coordinates are outside [-180, 180] range (indicates antimeridian crossing)
    const longitudes = coordinates.map((coord) => coord[0]);
    result.crossesAntimeridian = longitudes.some(
      (lon) => lon < -180 || lon > 180
    );

    return result;
  } catch {
    return result;
  }
}

/**
 * Checks if an OpenLayers LineString geometry has validation issues.
 *
 * @param geometry - OpenLayers LineString geometry to check
 * @returns Object with validation results
 */
export function hasLineStringValidationIssues(
  geometry: OlLineString
): LineStringValidationResult {
  const rawCoordinates = geometry.getCoordinates();

  // Transform coordinates from Web Mercator (EPSG:3857) to Geographic (EPSG:4326)
  // Use transform() instead of toLonLat() to preserve extended coordinates (±540°)
  const coordinates = rawCoordinates.map(
    (coord) => transform(coord, 'EPSG:3857', 'EPSG:4326') as number[]
  );

  return checkLineStringValidation(coordinates);
}

/**
 * Gets warning messages for linestring validation issues.
 * Single source of truth for linestring warnings.
 *
 * @param coordinates - LineString coordinates in EPSG:4326 (lon/lat degrees)
 * @returns Warning message string or null if no warnings
 */
export function getLineStringWarnings(coordinates: number[][]): string | null {
  const result = checkLineStringValidation(coordinates);
  const warnings: string[] = [];

  if (result.crossesAntimeridian) {
    warnings.push(
      'This shape appears to cross the International Date Line (180° longitude).'
    );
  }
  if (result.hasExcessiveLongitudeSpan) {
    warnings.push(
      'This line spans more than the entire world. Did you accidentally cross the antimeridian?'
    );
  }
  if (result.hasOutOfRangeCoordinates) {
    warnings.push(
      `Coordinates must be within ±${MAX_LONGITUDE}° longitude. Some coordinates are outside this range.`
    );
  }

  return warnings.length > 0 ? warnings.join(' ') : null;
}
